import { MemberRequest } from '@entities/Member/schema';
import { MemberFront } from '@entities/Member/types';
import { ENTITIES } from '@entities/enum';
import { FilterGroup } from '@features/ui-filters/components/types';
import { membersApi } from '@services/api/endpoints/members';
import { ACTIONS, PaginatedResponse } from '@root/src/types/common';
import { infiniteQueryOptions, mutationOptions, QueryClient, QueryKey, queryOptions } from '@tanstack/react-query';

/**
 * Members (the directory's entities) in the React Query cache: under which key each member
 * read lives, and which reads each member write leaves stale. Components and hooks read and
 * write members through this module only; no member key is spelled anywhere else.
 */

/** A page of `GET /members`, exactly as it is requested: the key holds what the fetch sends. */
export type MemberListParams = {
  page?: number;
  pageSize?: number;
  filters?: FilterGroup;
};

/** The member pickers scroll through `GET /members` page by page; the page is the scroll's. */
export type MemberInfiniteListParams = {
  filters?: FilterGroup;
};

/**
 * Every key starts with `memberKeys.all`, then names the resource. A write invalidates a
 * resource scope (`detail(id)`, `lists()`), which prefix-matches every read of that resource
 * and nothing else:
 * - `lists()` is every read of several members: the directory pages (entities, members),
 *   the member pickers' infinite scroll, and the related-members picker's selection;
 * - `detail(id)` is one member.
 */
export const memberKeys = {
  all: ['members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (params: MemberListParams) => [...memberKeys.lists(), params] as const,
  infiniteList: (params: MemberInfiniteListParams) => [...memberKeys.lists(), 'infinite', params] as const,
  selection: (memberIds: string[]) => [...memberKeys.lists(), 'selection', memberIds] as const,
  detail: (memberId: string) => [...memberKeys.all, 'detail', memberId] as const,
};

const THIRTY_SECONDS = 30 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Every member read. What belongs to the read itself lives here: key, fetch, stale time,
 * refetch policy, and `enabled` only as "no id, no fetch".
 */
export const memberQueries = {
  /**
   * One member. A dead reference (a deleted member, a historical ETL row) answers 404: the
   * pages that show one (bylines, the member page) handle it, so the global error handler
   * shows no toast for it (`meta.skipErrorToastOn404`, read in `handleGenericQueryErrors`).
   *
   * An errored query refetches on window focus by default, so after browsing a few pages
   * one focus event refired every accumulated byline 404 concurrently: enough simultaneous
   * requests to OOM-kill the 400 MB UAT backend. A 404 is a stable answer; it is never
   * refired automatically.
   */
  detail: (memberId: string) =>
    queryOptions({
      queryKey: memberKeys.detail(memberId),
      queryFn: () => membersApi.getMember(memberId),
      enabled: !!memberId,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      meta: { skipErrorToastOn404: true },
    }),
  /** Every directory page is read with a 30 s stale time. */
  list: (params: MemberListParams) =>
    queryOptions({
      queryKey: memberKeys.list(params),
      queryFn: () => membersApi.getMembers(params),
      staleTime: THIRTY_SECONDS,
    }),
  /** The member pickers: `GET /members` page by page, as far as the user scrolls. */
  infiniteList: (params: MemberInfiniteListParams) =>
    infiniteQueryOptions({
      queryKey: memberKeys.infiniteList(params),
      initialPageParam: 1,
      queryFn: ({ pageParam }) => membersApi.getMembers({ ...params, page: pageParam }),
      getNextPageParam: (lastPage: PaginatedResponse<MemberFront>, allPages: PaginatedResponse<MemberFront>[]) =>
        allPages.length < lastPage.pages ? allPages.length + 1 : undefined,
      staleTime: THIRTY_SECONDS,
    }),
  /**
   * The members a related-members picker holds, read one by one (`GET /members/:id`). A dead
   * reference (a deleted member) resolves to `null`, so it fails neither the read nor the page.
   */
  selection: (memberIds: string[]) =>
    queryOptions({
      queryKey: memberKeys.selection(memberIds),
      queryFn: () => Promise.all(memberIds.map(id => membersApi.getMember(id).catch(() => null))),
      enabled: memberIds.length > 0,
      staleTime: FIVE_MINUTES,
    }),
};

const invalidate = async (queryClient: QueryClient, keys: QueryKey[]): Promise<void> => {
  await Promise.all(keys.map(queryKey => queryClient.invalidateQueries({ queryKey })));
};

/**
 * What each member write leaves stale: the one place a member write's refresh targets are
 * declared. The mutations below call them.
 *
 * No other entity's write refreshes members, and no member write refreshes another entity.
 */
export const memberInvalidations = {
  /** The new member shows in the lists, and its page opens from the saved response. */
  created: (queryClient: QueryClient, member: MemberFront) => {
    queryClient.setQueryData(memberKeys.detail(member.id), member);
    return invalidate(queryClient, [memberKeys.lists()]);
  },
  updated: (queryClient: QueryClient, memberId: string) =>
    invalidate(queryClient, [memberKeys.detail(memberId), memberKeys.lists()]),
};

/**
 * Every member write. Each refreshes through `memberInvalidations`, on the client that ran it
 * (`useMutation`'s own client, handed to `onSuccess`), so `useMutation(memberMutations.x())`
 * needs no `useQueryClient`.
 *
 * The refresh is requested, not awaited: the form redirects to the member page as soon as
 * the save answers, as it always has, and awaiting would keep it pending until the lists
 * came back.
 */
export const memberMutations = {
  create: () =>
    mutationOptions<MemberFront, Error, MemberRequest>({
      mutationFn: request => membersApi.createMember(request),
      meta: { entity: ENTITIES.MEMBER, action: ACTIONS.CREATE },
      onSuccess: (member, _, __, { client }) => {
        void memberInvalidations.created(client, member);
      },
    }),
  update: (memberId: string) =>
    mutationOptions<MemberFront, Error, MemberRequest>({
      mutationFn: request => membersApi.updateMember(request, memberId),
      meta: { entity: ENTITIES.MEMBER, action: ACTIONS.UPDATE },
      onSuccess: (_, __, ___, { client }) => {
        void memberInvalidations.updated(client, memberId);
      },
    }),
  /**
   * Refreshes nothing, as before this module: the directory the user is sent back to keeps
   * its cached page (30 s stale time) until it refetches.
   */
  delete: () =>
    mutationOptions<void, Error, string>({
      mutationFn: memberId => membersApi.deleteMember(memberId),
      meta: { entity: ENTITIES.MEMBER, action: ACTIONS.DELETE },
    }),
};
